(ns hai.entity.related
  "What an entity links to, summed up as a pill — the lead item's glyph beside
   the count of them all — that opens, on hover or tap, a popover listing every
   one grouped by kind: links, tasks, notes, contacts, tags, spaces. A view
   only: rows navigate, nothing edits."
  (:require
   [clojure.string :as str]
   [hai.contacts.helpers :as contacts]
   [hai.ui.components :as c]
   [hai.ui.icons :as icons]
   [hai.ui.tokens :as t]
   [lambdaisland.ornament :as o]
   [reagent.core :as r]
   [url.core :as url]))

(def ^:private tag-icon (icons/mui-icon :outlined :label))
(def ^:private space-icon (icons/mui-icon :outlined :workspaces))

;; A top-level domain stands in for the site: no icon says "site" without
;; saying "building" or "globe", and the globe is Everywhere's.
(o/defstyled site-mark :span
  {:font-family "ui-monospace, SFMono-Regular, Menlo, monospace"
   :font-size "10px"
   :font-weight "700"
   :letter-spacing "-0.04em"
   :line-height "1"})

(defn- flat-label
  "Note bodies run over lines; a row shows them as one."
  [text fallback]
  (let [flat (some-> text (str/replace #"\s+" " ") str/trim)]
    (if (str/blank? flat) fallback flat)))

(defn- match-rank [match]
  (case match
    :page 0
    :site 1
    2))

(defn- items
  "Every item in `related` as a popover row, grouped by kind. The links lead
   with whichever `link-match` — the host's `(fn [url])` → `:page`, `:site` or
   nil — places on the page it is showing, then the rest as stored."
  [{:keys [link-urls contacts notes tasks tags spaces]} link-match]
  (concat
   (->> link-urls
        (map (fn [link-url]
               {:kind  :link
                :key   link-url
                :url   link-url
                :match (when link-match (link-match link-url))
                :label (or (url/url->display-label link-url) link-url)}))
        (sort-by (comp match-rank :match)))
   (for [task tasks]
     {:kind :task :key (:task/uuid task) :entity task
      :label (flat-label (:task/title task) "Untitled task")})
   (for [note notes]
     {:kind :note :key (:note/uuid note) :entity note
      :label (flat-label (:note/text note) "Untitled note")})
   (for [contact contacts]
     {:kind :contact :key (:contact/uuid contact) :entity contact
      :label (flat-label (:contact/full-name contact) "Unnamed contact")})
   (for [tag tags]
     (let [tag-name (or (:group/display-name tag) (:tag/name tag))]
       {:kind :tag :key (or (:group/uuid tag) tag-name) :label tag-name}))
   (for [space spaces]
     {:kind :space :key (:space/uuid space) :entity space
      :label (flat-label (:space/title space) "Untitled space")})))

(defn- glyph [{:keys [kind match entity]}]
  (case kind
    :link    (if (= :site match)
               [site-mark ".com"]
               [icons/outlined-link])
    :task    [icons/humble-task]
    :note    [icons/humble-note]
    :contact [(contacts/type-icon entity)]
    :tag     [tag-icon]
    :space   [space-icon]))

;; One tint per kind, so a kind is told apart at a glance — on a row's tile and
;; on the pill's lead glyph alike: grey links, green tasks, yellow notes, orange
;; contacts, taupe tags, blue spaces.
(o/defstyled kind-tint :span
  [:&.link {:background t/--card-forest-10 :color t/--card-forest-70}]
  [:&.task {:background t/--tasks :color t/--task-accent}]
  [:&.note {:background t/--notes :color t/--card-warning-text}]
  [:&.contact {:background t/--card-orange-soft :color t/--card-orange}]
  [:&.tag {:background t/--card-taupe-soft :color t/--card-taupe}]
  [:&.space {:background t/--card-info-12 :color t/--card-info}])

;; The resting summary on the card: the lead item's glyph and the count, as one
;; split pill.
(o/defstyled pill :button
  {:display "inline-flex" :align-items "stretch"
   :box-sizing "border-box"
   :height "26px" :padding "0"
   :border-width "1px" :border-style "solid" :border-color t/--card-forest-14
   :border-radius "8px"
   :overflow "hidden"
   :background "transparent"
   :font-family "inherit"
   :color t/--card-forest
   :cursor "pointer"
   :transition "border-color 0.15s ease"}
  [:&:hover {:border-color t/--note-sage-55}]
  [:.pill-glyph
   {:display "inline-flex" :align-items "center" :justify-content "center"
    :padding "0 8px"
    :border-right-width "1px" :border-right-style "solid"
    :border-right-color t/--card-forest-14}
   [:svg {:width "14px" :height "14px" :fill "currentColor"}]]
  [:.pill-count
   {:display "inline-flex" :align-items "center"
    :padding "0 9px"
    :font-size "12.5px" :font-weight "700"}])

;; A row's glyph on a small tile, tinted by kind.
(o/defstyled tile kind-tint
  {:display "inline-flex" :align-items "center" :justify-content "center"
   :flex "0 0 auto"
   :width "26px" :height "26px"
   :border-radius "7px"}
  [:svg {:width "15px" :height "15px" :fill "currentColor"}])

(o/defstyled popover :div
  {:width "300px" :max-width "calc(100vw - 32px)"}
  [:.related-head
   {:display "flex" :align-items "center" :justify-content "space-between"
    :margin-bottom "6px"}]
  [:.related-title
   {:font-size "11px" :font-weight "700"
    :letter-spacing "0.08em" :text-transform "uppercase"
    :color t/--card-forest-70}]
  [:.related-close
   {:width "28px" :height "28px" :padding "0"
    :display "inline-flex" :align-items "center" :justify-content "center"
    :border "none" :border-radius "8px"
    :background t/--card-forest-06
    :color t/--card-forest-70
    :cursor "pointer"}
   [:&:hover {:background t/--card-forest-10 :color t/--card-forest}]
   [:svg {:fill "currentColor"}]]
  ;; Groups sit a little apart, so a kind reads as a block.
  [:.related-list
   {:display "flex" :flex-direction "column" :gap "8px"
    :max-height "280px" :overflow-y "auto"
    :margin "0 -6px"}]
  [:.related-group
   {:display "flex" :flex-direction "column" :gap "2px"}]
  [:.related-row
   {:display "flex" :align-items "center" :gap "10px"
    :box-sizing "border-box" :width "100%" :min-width "0"
    :padding "5px 6px"
    :border "none" :border-radius "8px"
    :background "transparent"
    :font-family "inherit" :font-size "14px" :text-align "left"
    :color t/--card-forest
    :text-decoration "none"}]
  [#{:a.related-row :button.related-row}
   {:cursor "pointer"}
   [:&:hover {:background t/--card-forest-06}]]
  [:.related-label
   {:flex "1" :min-width "0"
    :overflow "hidden" :text-overflow "ellipsis" :white-space "nowrap"}])

(defn- related-row [{:keys [kind url entity label] :as row} {:keys [on-open-entity close!]}]
  (let [row-glyph [tile {:class (name kind)} (glyph row)]
        row-label [:span.related-label label]]
    (cond
      (= :link kind)
      [:a.related-row {:href url :target "_blank" :rel "noopener noreferrer" :title url}
       row-glyph row-label]

      (and entity on-open-entity)
      [:button.related-row {:type "button"
                            :title label
                            :on-click (fn []
                                        (close!)
                                        (on-open-entity entity))}
       row-glyph row-label]

      :else
      [:div.related-row {:title label}
       row-glyph row-label])))

(def ^:private open-delay-ms 200)
(def ^:private close-delay-ms 150)

(defn- touch? [e]
  (= "touch" (.-pointerType e)))

(defn component
  "The pill over `related` — `{:link-urls :contacts :notes :tasks :tags
   :spaces}` — and its popover; nothing when there is nothing related. The
   popover opens on hover and closes on leaving it; a tap opens it too, and its
   ✕ closes it. `link-match`, if supplied, leads the links with the ones on the
   page the host is showing (see [[items]]); `on-open-entity`, if supplied,
   opens a linked task, note, contact or space — absent, those rows are plain
   text. Clicks stay inside: the pill sits on pressable cards."
  [{:keys [related link-match on-open-entity]}]
  (r/with-let [!open? (r/atom false)
               !timer (atom nil)
               set-open! (fn [open?]
                           (some-> @!timer js/clearTimeout)
                           (reset! !open? open?))
               set-open-later! (fn [open? delay-ms]
                                 (some-> @!timer js/clearTimeout)
                                 (reset! !timer (js/setTimeout #(reset! !open? open?) delay-ms)))
               close! #(set-open! false)
               ;; A Popover rather than a HoverCard, whose trigger prevents
               ;; touchstart's default — refused in React's passive listener,
               ;; and logged. Hovering opens it after a beat and leaving closes
               ;; it after one, long enough to cross onto the popover; touch
               ;; has no hover, so a tap opens it and ✕ closes it.
               on-enter (fn [e]
                          (when-not (touch? e)
                            (set-open-later! true open-delay-ms)))
               on-leave (fn [e]
                          (when-not (touch? e)
                            (set-open-later! false close-delay-ms)))]
    (let [rows (items related link-match)]
      (when (seq rows)
        ;; The popover is portalled, but its clicks still bubble through the
        ;; React tree to the card.
        [:span {:on-click #(.stopPropagation %)}
         [c/PopoverRoot {:open @!open?
                         :onOpenChange (fn [open?]
                                         (when-not open?
                                           (close!)))}
          [c/PopoverTrigger
           [:button {:type "button"
                     :class (str pill)
                     :aria-label (str "Related to " (count rows))
                     :on-pointer-enter on-enter
                     :on-pointer-leave on-leave
                     ;; Opens rather than toggles, so a click on a pill hover
                     ;; has opened keeps it open: Radix skips its own toggle
                     ;; for a prevented click.
                     :on-click (fn [e]
                                 (.preventDefault e)
                                 (set-open! true))}
            [kind-tint {:class ["pill-glyph" (name (:kind (first rows)))]}
             (glyph (first rows))]
            [:span.pill-count (count rows)]]]
          [c/PopoverContent {:side "bottom" :align "end" :sideOffset 6 :size "1"
                             :on-pointer-enter on-enter
                             :on-pointer-leave on-leave
                             ;; A hover mustn't pull the focus in, nor a close
                             ;; push it onto the pill.
                             :onOpenAutoFocus #(.preventDefault %)
                             :onCloseAutoFocus #(.preventDefault %)}
           [popover
            [:div.related-head
             [:span.related-title "Related to"]
             [:button.related-close {:type "button" :aria-label "Close" :on-click close!}
              [icons/outlined-close {:size 16}]]]
            (into [:div.related-list]
                  (for [group (partition-by :kind rows)]
                    ^{:key (:kind (first group))}
                    (into [:div.related-group]
                          (for [row group]
                            ^{:key (:key row)}
                            [related-row row {:on-open-entity on-open-entity
                                              :close! close!}]))))]]]]))
    (finally
      (some-> @!timer js/clearTimeout))))
